import { useMe } from "@/api/services/admin/auth";
import { IconBackArrow, IconChevronDown, IconForwardArrow, IconLogo } from "@/assets/icons";
import { Modal } from "@/components/elements/Modal";
import { Popover } from "@/components/elements/Popover";
import { RuntimeEnvTag } from "@/components/elements/RuntimeEnvTag";
import { useDialog } from "@/hooks/useDialog";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { AppRoutes } from "@/routes/config";
import { black, divider, text } from "@/theme/colors";
import { elevation } from "@/theme/variables";
import { Box, Button, Divider, Stack, styled, Tooltip, Typography } from "@mui/material";
import zIndex from "@mui/material/styles/zIndex";
import { FC } from "react";
import { Link, useNavigate } from "react-router-dom";

type TAppHeaderProps = {
  isOpenDrawer: boolean;
  toggleDrawer: () => void;
};

const CENTER_URL = (process.env.REACT_APP_ADMIN_CENTER_URL ?? "").replace(/\/$/, "");

export const AppHeader: FC<TAppHeaderProps> = ({ isOpenDrawer, toggleDrawer }) => {
  const { isLaptop } = useMediaQuery();
  const navigate = useNavigate();
  const { data: meResult } = useMe();
  const meData = meResult?.data;
  const logoutDialog = useDialog();

  const handleLogout = async () => {
    const agreedToLogout = await logoutDialog.show();
    if (!agreedToLogout) return;
    navigate(AppRoutes.logout);
  };

  const goToCenter = () => {
    if (!CENTER_URL) return;
    window.location.href = CENTER_URL;
  };

  return (
    <BoxWrapper>
      <Stack flexDirection="row" alignItems="center" gap={3}>
        {isLaptop && (
          <Tooltip title={isOpenDrawer ? "メニューを最小化" : "メニューを最大化"} placement="bottom-start">
            <Button sx={{ minWidth: "auto", paddingX: 0 }} onClick={toggleDrawer}>
              {isOpenDrawer ? <IconBackArrow width={32} height={32} /> : <IconForwardArrow width={32} height={32} />}
            </Button>
          </Tooltip>
        )}
        <Stack flex={1} flexDirection="row" justifyContent="space-between" alignItems="center">
          <Stack flexDirection="row" alignItems="flex-end">
            <StyledLink to={AppRoutes.home}>
              <Stack flexDirection="row" alignItems="flex-end">
                <IconLogo />
                <Typography variant="sub16Bold" color={text.white} sx={{ marginLeft: "8px" }}>
                  visa
                </Typography>
              </Stack>
            </StyledLink>
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                borderColor: "#E6E7E8",
                marginRight: "16px",
                marginLeft: "16px",
              }}
            />
            <Typography variant="head20Bold" color={text.white}>
              システム管理
            </Typography>
            <Box ml={2}>
              <RuntimeEnvTag />
            </Box>
          </Stack>
          <Stack direction="row" mt={1} justifyContent="center" alignItems="center">
            <Typography variant="body14" mr={1} mb={1} fontWeight={500} color={text.white}>
              {meData?.fullName}
            </Typography>
            <Popover
              sx={{
                marginTop: "8px",
                borderRadius: 0,
                ".MuiPopover-paper": {
                  borderRadius: "0 0 8px 8px",
                  boxShadow: "0px 4px 4px 0px #00000040",
                },
              }}
              anchor={<IconChevronDown cursor="pointer" fontSize="24" color={text.white} />}
            >
              <Stack>
                {CENTER_URL && <MenuOption onClick={goToCenter}>irohana center</MenuOption>}
                <MenuOption onClick={handleLogout}>ログアウト</MenuOption>
              </Stack>
            </Popover>
          </Stack>
        </Stack>
      </Stack>
      <Modal isOpen={logoutDialog.open} onClose={logoutDialog.cancel} onOk={logoutDialog.confirm} labelClose="いいえ" labelOk="はい" width={500}>
        <Stack mb={2}>
          <Typography>すべてのirohanaサービスからログアウトされますがよろしいですか？</Typography>
        </Stack>
      </Modal>
    </BoxWrapper>
  );
};

const BoxWrapper = styled(Box)`
  padding: 9px 24px;
  border-bottom: 1px solid #7f7f7f;
  background-color: ${black[30]};
  position: sticky;
  box-shadow: ${elevation.lv1};
  top: 0;
  z-index: ${zIndex.appBar};
`;

const MenuOption = styled(Box)`
  font-size: 14px;
  padding: 4px 28px;
  cursor: pointer;
  text-align: center;
  white-space: nowrap;
  :hover {
    background: ${divider.low};
    color: ${text.primary};
  }
  :focus {
    background: ${divider.middle};
    color: ${text.primary};
  }
`;

const StyledLink = styled(Link)`
  &.centered {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;
